import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { getSharedRouteId } from "@/lib/appEntryLink"
import AnalyzeOrderModal from "./AnalyzeOrderOnPageModal"
import { HTML5Backend } from "react-dnd-html5-backend"
import { DndProvider } from "react-dnd"
import { Button } from "@/components/ui/button"
import { uploadToS3 } from "@/components/analyzeOrderOnPage/uploadToS3"
import PlaneOrderOnPageModal from "@/components/analyzeOrderOnPage/PlanOrderOnPageModal"
import LeadCaptureModal from "@/components/analyzeOrderOnPage/LeadCaptureModal"
import { v4 as uuid } from "uuid"
import { getAPIBaseUrl, loadRouteState } from "@/lib/routeStateUtils"
import {
  getSavedLeadFormData,
  hasSubmittedLeadForm,
  isFirstVisit,
  shouldShowLeadModal,
} from "@/lib/leadCapture"
import { parse } from "@xmpp/jid"
import {
  setupAnonymousXmppConnection,
  stopAnonymousXmppConnection,
  XmppContext,
} from "@/stream/xmppClient"
import { APIProvider } from "@vis.gl/react-google-maps"
import AdHocCommands, { AvoidType, LoadType } from "@/api/adHocCommands"
import { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import html2canvas from "html2canvas"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  ANY_FORMAT_ORDERS_FLAG,
  getOrderFileAccept,
  isSupportedOrderFile,
} from "@/lib/orderFileSupport"
import { toast } from "sonner"
import AnonymousSidebar, {
  type AnonymousTab,
} from "@/components/analyzeOrderOnPage/AnonymousSidebar"
import AnonymousChat from "@/components/analyzeOrderOnPage/AnonymousChat"
import CookieConsentBanner from "@/components/analyzeOrderOnPage/CookieConsentBanner"
import { useAppDispatch, useAppStore } from "@/reducers/hooks"
import { upsertChat } from "@/reducers/chatsSlice"
import { addToSynced } from "@/reducers/roomSyncStatusSlice"
import XmppGroupChatApi from "@/api/xmppGroupChatApi"

const STORAGE_KEY = "ORDER_ANALYZE_ID"

/**
 * Get or create an anonymous user ID.
 * Uses our own localStorage as the source of truth, then syncs with Segment when ready.
 *
 * This ID is used for:
 * - Segment analytics identification
 * - XMPP connection resource (to correlate analytics with XMPP sessions)
 */
function getOrCreateAnonymousId(): string {
  try {
    const ls = window.localStorage
    let id = ls.getItem(STORAGE_KEY)

    if (!id) {
      id = uuid()
      ls.setItem(STORAGE_KEY, id)
    }

    return id
  } catch (e) {
    return uuid()
  }
}

/**
 * Build a room name from lead form data for the transchat room.
 */
function buildTranschatRoomName(): string | undefined {
  const leadData = getSavedLeadFormData()
  if (!leadData) return undefined
  const parts: string[] = []
  if (leadData.name) parts.push(leadData.name)
  if (leadData.email) parts.push(leadData.email)
  return parts.length > 0 ? parts.join(" - ") : undefined
}

/**
 * Sync our anonymous ID with Segment when it becomes ready.
 * This ensures Segment uses the same ID we use for XMPP.
 */
function syncAnonymousIdWithSegment(id: string): void {
  if (window.analytics?.ready) {
    window.analytics.ready(() => {
      // Set our ID as Segment's anonymous ID
      window.analytics?.setAnonymousId?.(id)
    })
  }
}

// Helper function to convert text to image file
const convertTextToImage = async (text: string): Promise<File> => {
  // Create a temporary div element with the text
  const tempDiv = document.createElement("div")
  tempDiv.style.position = "absolute"
  tempDiv.style.left = "-9999px"
  tempDiv.style.top = "-9999px"
  tempDiv.style.width = "800px"
  tempDiv.style.padding = "20px"
  tempDiv.style.fontSize = "16px"
  tempDiv.style.fontFamily = "Arial, sans-serif"
  tempDiv.style.lineHeight = "1.5"
  tempDiv.style.color = "#000"
  tempDiv.style.backgroundColor = "#fff"
  tempDiv.style.whiteSpace = "pre-wrap"
  tempDiv.style.wordWrap = "break-word"
  tempDiv.textContent = text

  // Add to document temporarily
  document.body.appendChild(tempDiv)

  try {
    // Convert to canvas
    const canvas = await html2canvas(tempDiv, {
      backgroundColor: "#ffffff",
      scale: 2, // Higher quality
      useCORS: true,
    })

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), "image/png")
    })

    // Create File object
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
    const file = new File([blob], `text-order-${timestamp}.png`, {
      type: "image/png",
    })

    return file
  } finally {
    // Clean up
    document.body.removeChild(tempDiv)
  }
}

function AnalyzeOrderOnPage() {
  const { t } = useTranslation()
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const anyFormatEnabled = Boolean(
    useFeatureFlagEnabled(ANY_FORMAT_ORDERS_FLAG),
  )
  const [searchParams] = useSearchParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(true) // Auto-open on load
  const [sharedRouteState, setSharedRouteState] = useState<any>(null)
  const [sharedDocumentStep, setSharedDocumentStep] = useState<string>("verify")
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLoadingSharedRoute, setIsLoadingSharedRoute] = useState(false)
  const [, setUploadedFile] = useState<File | null>(null)
  const [extractedData, setExtractedData] = useState<any>(null)
  const [error, setError] = useState<string>("")
  const [fileInputValue, setFileInputValue] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [xmppConnected, setXmppConnected] = useState(false)
  const [myJid, setMyJid] = useState<string | null>(null)

  // Selected options state (preserved when switching between flows)
  const [selectedAvoidOptions, setSelectedAvoidOptions] = useState<AvoidType[]>(
    [],
  )
  const [selectedDriver, setSelectedDriver] = useState<
    SelectedDriverLocation | undefined
  >(undefined)
  const [selectedLoadType, setSelectedLoadType] = useState<LoadType[]>([])
  const [selectedVehicleType, setSelectedVehicleType] =
    useState<VehicleType>("tir")

  // Text order dialog state
  const [isTextOrderDialogOpen, setIsTextOrderDialogOpen] = useState(false)
  const [textOrderContent, setTextOrderContent] = useState("")
  const [isProcessingTextOrder, setIsProcessingTextOrder] = useState(false)

  // Lead capture modal state
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false)
  const [hasSharedRoute, setHasSharedRoute] = useState(false)
  const [isFirstTimeVisitor, setIsFirstTimeVisitor] = useState(false)
  const leadModalShownRef = useRef(false)
  const leadModalTriggerRef = useRef<string | null>(null)
  const chatMessageCountRef = useRef(0)

  // AI chat room state
  const [transchatRoomJid, setTranschatRoomJid] = useState<string | null>(null)

  // Sidebar tab state
  const tabParam = searchParams.get("tab")
  const [activeTab, setActiveTab] = useState<AnonymousTab>(
    getSharedRouteId(searchParams.toString())
      ? "plan"
      : tabParam === "ai"
        ? "ai"
        : "plan",
  )

  // Setup anonymous XMPP connection immediately
  useEffect(() => {
    const anonymousId = getOrCreateAnonymousId()

    setupAnonymousXmppConnection(anonymousId, store, {
      onOnline: (jid) => {
        setMyJid(jid.toString())
        setXmppConnected(true)
      },
      onOffline: () => {
        setXmppConnected(false)
      },
    })

    return () => {
      stopAnonymousXmppConnection()
    }
  }, [store])

  // Ensure anonymous transchat room on XMPP connect (with retry for session race)
  useEffect(() => {
    if (!xmppConnected || !window.XMPP) return
    const userId = getOrCreateAnonymousId()
    const roomName = buildTranschatRoomName()
    let cancelled = false

    const attempt = (retries: number) => {
      if (cancelled || !window.XMPP) return
      AdHocCommands.ensureAnonymousTranschat(window.XMPP, userId, roomName)
        .then((roomJid) => {
          if (cancelled) return
          dispatch(
            upsertChat({
              chat: { jid: roomJid, name: "Slick AI", room: true },
            }),
          )
          // Anonymous rooms have no MAM history to sync; mark synced
          // immediately so SendoutQueueWorker's room-sync gate lets
          // outgoing messages through on the first send.
          dispatch(addToSynced({ jid: roomJid, wasFullSynced: true }))
          setTranschatRoomJid(roomJid)

          // Join MUC so we receive groupchat stanzas
          const myJidParsed = window.XMPP?.jid
          if (myJidParsed && window.XMPP) {
            const nick =
              myJidParsed.local || myJidParsed.resource || "anonymous"
            XmppGroupChatApi.sendPresence(
              window.XMPP,
              roomJid,
              nick,
              new Date(0),
            ).catch((err) => {
              console.error("Failed to join anonymous MUC room:", err)
              window.analytics?.track("Anonymous_ChatJoinFailed", {
                roomJid,
                error: err instanceof Error ? err.message : String(err),
              })
            })
          }
        })
        .catch((err) => {
          if (cancelled) return
          if (retries > 0) {
            setTimeout(() => attempt(retries - 1), 2000)
          } else {
            console.error("Failed to ensure anonymous transchat:", err)
            window.analytics?.track("Anonymous_TranschatFailed", {
              error: err instanceof Error ? err.message : String(err),
            })
          }
        })
    }
    attempt(3)

    return () => {
      cancelled = true
    }
  }, [xmppConnected, dispatch])

  // Re-join MUC room after XMPP reconnect (suspend/resume, network drop).
  // Without this the user stops receiving agent replies after the first drop —
  // ejabberd evicts the occupancy when the session goes away and a fresh
  // groupchat send gets bounced as not-acceptable.
  useEffect(() => {
    if (!transchatRoomJid || !myJid) return
    const xmpp = window.XMPP
    if (!xmpp) return
    const myJidParsed = parse(myJid)
    const nick = myJidParsed.local || myJidParsed.resource || "anonymous"
    const handleOnline = () => {
      XmppGroupChatApi.sendPresence(
        xmpp,
        transchatRoomJid,
        nick,
        new Date(0),
      ).catch((err) =>
        console.error("Failed to re-join anonymous MUC after reconnect:", err),
      )
    }
    xmpp.on("online", handleOnline)
    return () => {
      xmpp.off("online", handleOnline)
    }
  }, [transchatRoomJid, myJid])

  // Leave the room on unmount (best-effort) so we don't leave a ghost occupant
  // behind. Ejabberd will GC stale occupancy on session drop anyway, but this
  // is cleaner and matches the pre-migration useAnonymousMucChat behaviour.
  useEffect(() => {
    if (!transchatRoomJid || !myJid) return
    return () => {
      const xmpp = window.XMPP
      if (!xmpp) return
      const myJidParsed = parse(myJid)
      const nick = myJidParsed.local || myJidParsed.resource || "anonymous"
      XmppGroupChatApi.sendRoomExitPresence(xmpp, transchatRoomJid, nick).catch(
        () => {},
      )
    }
  }, [transchatRoomJid, myJid])

  // Track lead capture abandonment when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (leadModalShownRef.current && !hasSubmittedLeadForm()) {
        window.analytics?.track("LeadCapture_PageAbandoned", {
          trigger: leadModalTriggerRef.current,
        })
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [])

  const xmppContextValue = useMemo(() => {
    if (!window.XMPP || !myJid) {
      // Return a placeholder value - components should check for client/myJid validity
      return {} as any
    }
    return {
      client: window.XMPP,
      myJid: parse(myJid),
    }
  }, [myJid, xmppConnected])

  const resetComponentState = () => {
    setIsProcessing(false)
    setUploadedFile(null)
    setExtractedData(null)
    setError("")
    setFileInputValue("")
    setSharedRouteState(null)
    setSharedDocumentStep("verify")
    setIsModalOpen(false)
    setIsPlanModalOpen(true)
    setFileName(null)
    setFileUrl(null)
    setSelectedDriver(undefined)
    setSelectedAvoidOptions([])
    setSelectedLoadType([])
    setSelectedVehicleType("tir")
    setIsTextOrderDialogOpen(false)
    setTextOrderContent("")
    setIsProcessingTextOrder(false)
  }

  useEffect(() => {
    // Get our anonymous ID from localStorage (or generate new)
    const anonymousId = getOrCreateAnonymousId()

    // Sync our ID with Segment when it becomes ready
    syncAnonymousIdWithSegment(anonymousId)

    document.documentElement.classList.remove("bp5-dark")
    document.documentElement.classList.remove("dark")

    // Check if first time visitor and track analytics
    const firstVisit = isFirstVisit()
    setIsFirstTimeVisitor(firstVisit)
    const hasSharedRoute = Boolean(getSharedRouteId(searchParams.toString()))

    if (firstVisit) {
      window.analytics?.track("Anonymous_FirstVisit", {
        anonymousId,
        referrer: document.referrer || "direct",
        hasSharedRoute,
      })
    }

    // Track page view for unique visitors
    window.analytics?.track("Anonymous_PageView", {
      anonymousId,
      hasSharedRoute,
    })
  }, [searchParams])

  useLayoutEffect(() => {
    const load = async () => {
      // Check for shared route in query params
      const routeId = getSharedRouteId(searchParams.toString())
      if (routeId) {
        setHasSharedRoute(true)
        setIsLoadingSharedRoute(true)
        try {
          const savedState = await loadRouteState(routeId)
          if (savedState) {
            // Check if this is document extraction data or route planning data
            if (
              savedState.extractedData &&
              savedState.fileName &&
              savedState.fileUrl
            ) {
              setSharedRouteState(savedState)
              // Handle shared document data
              setExtractedData(savedState.extractedData)
              setFileName(savedState.fileName)
              setFileUrl(savedState.fileUrl)
              setSharedDocumentStep("map")
              setIsModalOpen(true)
              window.analytics?.track("Anonymous_SharedDocumentLoaded", {
                routeId,
                fileName: savedState.fileName,
                stopsCount: savedState.extractedData?.stops?.length || 0,
              })
            } else {
              // Handle shared route planning data
              setSharedRouteState(savedState)
              setIsPlanModalOpen(true)
              window.analytics?.track("Anonymous_SharedRouteLoaded", {
                routeId,
                routeStopsCount: savedState.temporaryRoute?.stops?.length || 0,
              })
            }
          } else {
            console.warn("Shared route not found or expired:", routeId)
            window.analytics?.track("Anonymous_SharedRouteNotFound", {
              routeId,
            })
            // Lead modal will be shown on second stop or chat message attempt
          }
        } catch (error) {
          console.error("Failed to load shared route:", error)
          window.analytics?.track("Anonymous_SharedRouteLoadFailed", {
            routeId,
            error: error instanceof Error ? error.message : String(error),
          })
        } finally {
          setIsLoadingSharedRoute(false)
        }
      } else {
        // Lead modal will be shown on second stop or chat message attempt
      }
    }
    load()
  }, [searchParams])

  useEffect(() => {
    // Modify body styles
    document.body.style.overflowX = "hidden"
    document.body.style.overflowY = "visible"
    document.body.style.scrollbarGutter = "stable"

    // Cleanup function to restore original styles
    return () => {
      document.body.style.overflow = "hidden"
    }
  })

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
    // Reset file input value to allow selecting the same file again
    setFileInputValue("")
  }

  // Handle switching to analyze order flow (file upload)
  const handleSwitchToAnalyzeOrder = useCallback(
    ({
      avoid,
      loadType,
      vehicleType,
      selectedDriver,
      file,
    }: {
      avoid: AvoidType[]
      loadType: LoadType[]
      vehicleType: VehicleType
      selectedDriver: SelectedDriverLocation | undefined
      // Already-picked document (drag & drop) — process it directly instead of
      // opening the file picker.
      file?: File
    }) => {
      setSelectedDriver(selectedDriver)
      setSelectedAvoidOptions(avoid)
      setSelectedLoadType(loadType)
      setSelectedVehicleType(vehicleType)
      if (file) {
        handleFileSelect(file)
      } else {
        fileInputRef.current?.click()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Handle opening text order dialog
  const handleOpenTextOrder = useCallback(
    ({
      avoid,
      loadType,
      vehicleType,
      selectedDriver,
    }: {
      avoid: AvoidType[]
      loadType: LoadType[]
      vehicleType: VehicleType
      selectedDriver: SelectedDriverLocation | undefined
    }) => {
      setSelectedDriver(selectedDriver)
      setSelectedAvoidOptions(avoid)
      setSelectedLoadType(loadType)
      setSelectedVehicleType(vehicleType)
      setIsTextOrderDialogOpen(true)
      window.analytics?.track("Anonymous_TextOrderOpened")
    },
    [],
  )

  // Handle closing text order dialog
  const handleCloseTextOrder = useCallback(() => {
    setIsTextOrderDialogOpen(false)
    setTextOrderContent("")
    setIsProcessingTextOrder(false)
  }, [])

  // Handle dialog open/close changes (for ESC, clicking outside, etc.)
  const handleTextOrderDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Clear content when closing dialog by any method
      setTextOrderContent("")
      setIsProcessingTextOrder(false)
    }
    setIsTextOrderDialogOpen(open)
  }, [])

  // Handle submitting text order
  const handleSubmitTextOrder = useCallback(async () => {
    if (!textOrderContent.trim()) {
      toast.warning(t("planOrder.textOrder.empty"))
      return
    }

    window.analytics?.track("Anonymous_TextOrderSubmitted", {
      textLength: textOrderContent.trim().length,
    })
    setIsProcessingTextOrder(true)
    try {
      const text = textOrderContent.trim()
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
      // With any-format-orders the backend accepts plain text directly;
      // otherwise fall back to rasterizing the text to a PNG.
      const orderFile = anyFormatEnabled
        ? new File([text], `text-order-${timestamp}.txt`, {
            type: "text/plain",
          })
        : await convertTextToImage(text)

      // Close dialog first
      handleCloseTextOrder()

      // Process the file using existing handleFileSelect
      handleFileSelect(orderFile)
    } catch (err) {
      console.error("Error processing text order:", err)
      toast.error(t("planOrder.textOrder.failed"))
      setIsProcessingTextOrder(false)
    }
  }, [textOrderContent, handleCloseTextOrder, anyFormatEnabled, t])

  // Show lead modal when user adds a second stop
  const handleRouteModified = useCallback((stopCount: number) => {
    if (stopCount >= 2 && shouldShowLeadModal() && !leadModalShownRef.current) {
      leadModalShownRef.current = true
      leadModalTriggerRef.current = "second_stop_added"
      setIsLeadModalOpen(true)
      window.analytics?.track("LeadCapture_ModalShown", {
        trigger: "second_stop_added",
      })
    }
  }, [])

  // Show lead modal after user sends their second chat message
  const showLeadModalForChat = useCallback(() => {
    chatMessageCountRef.current += 1
    if (
      chatMessageCountRef.current >= 2 &&
      shouldShowLeadModal() &&
      !leadModalShownRef.current
    ) {
      leadModalShownRef.current = true
      leadModalTriggerRef.current = "second_chat_message"
      setIsLeadModalOpen(true)
      window.analytics?.track("LeadCapture_ModalShown", {
        trigger: "second_chat_message",
      })
    }
    return false // never block — message sends immediately, modal appears on top
  }, [])

  // Handle lead modal close — rename transchat room with user data (delayed to avoid
  // interrupting the AI composing state — the server rename can broadcast stanzas that
  // clear the composing indicator)
  const handleLeadModalClose = useCallback(() => {
    setIsLeadModalOpen(false)
    const roomName = buildTranschatRoomName()
    if (roomName && xmppConnected && window.XMPP) {
      const userId = getOrCreateAnonymousId()
      setTimeout(() => {
        if (window.XMPP) {
          AdHocCommands.renameAnonymousTranschat(
            window.XMPP,
            userId,
            roomName,
          ).catch((err) =>
            console.error("Failed to rename anonymous transchat:", err),
          )
        }
      }, 5000)
    }
  }, [xmppConnected])

  // Handle route link click from AI chat — load route and switch to plan tab
  const handleRouteLink = useCallback(
    async (routeId: string) => {
      try {
        const savedState = await loadRouteState(routeId)
        if (savedState) {
          setSharedRouteState(savedState)
          setIsPlanModalOpen(true)
          setActiveTab("plan")
          window.analytics?.track("Anonymous_ChatRouteLinkClicked", {
            routeId,
          })
        } else {
          toast.error(t("planOrder.routeToasts.notFound"))
        }
      } catch (error) {
        console.error("Failed to load route from chat link:", error)
        toast.error(t("planOrder.routeToasts.loadFailed"))
      }
    },
    [t],
  )

  const handleFileSelect = async (file: File) => {
    if (!isSupportedOrderFile(file, anyFormatEnabled)) {
      const message = anyFormatEnabled
        ? t("planOrder.fileErrors.unsupportedFormat")
        : t("planOrder.fileErrors.imageOrPdf")
      setError(message)
      toast.error(message)
      return
    }

    window.analytics?.track("Anonymous_FileSelected", {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    })
    setUploadedFile(file)
    setIsProcessing(true)
    setError("")
    setFileName(file.name)

    // Close plan modal and open analyze modal
    setIsPlanModalOpen(false)
    setIsModalOpen(true)

    try {
      const { getUrl, fileName } = await uploadToS3(file)
      setFileUrl(getUrl)
      setFileName(fileName)
      setIsModalOpen(true)
      // Extract metadata
      const encodedUrl = encodeURIComponent(getUrl)

      const baseUrl =
        window.location.hostname !== "app.trickswift.eu"
          ? getAPIBaseUrl()
          : "https://api.trickswift.eu/production"

      const extractResponse = await fetch(
        `${baseUrl}/extract_delivery_order_doc_metadata?url=${encodedUrl}`,
      )

      if (!extractResponse.ok) {
        throw new Error("API call failed")
      }

      const response = await extractResponse.json()

      const data = {
        ...response,
        stops: response.stops.map((stop: any) => {
          const formattedAddress = stop.formatted_address
            ? stop.formatted_address
            : [
                stop.street && stop.street_number
                  ? `${stop.street} ${stop.street_number}`
                  : stop.street,
                stop.postal_code,
                stop.city,
                stop.country,
              ]
                .filter(Boolean)
                .join(", ")

          return {
            action: stop.action,
            scheduled_arrival_time: `${stop.scheduled_date.date}T${stop.scheduled_date.time ?? "23:59:00"}`,
            scheduled_hour_min: stop.scheduled_date.time_min,
            scheduled_hour_max: stop.scheduled_date.time_max,
            location: {
              formattedAddress,
              companyName: stop.company_name,
              postalCode: stop.postal_code,
              lat: stop.lat != null ? String(stop.lat) : undefined,
              lon: stop.lng != null ? String(stop.lng) : undefined,
            },
            action_reference_number: stop.action_reference_number,
            pallet_count: stop.pallet_count,
            weight_kg: stop.weight_kg,
          }
        }),
      }

      setIsProcessing(false)
      setExtractedData(data)
      window.analytics?.track("Anonymous_MetaDataExtracted", {
        metaData: data,
      })
    } catch (err: unknown) {
      setIsModalOpen(false)
      console.error("Error processing file:", err)
      window.analytics?.track("Anonymous_MetaDataExtractingFailed", {
        error: err,
      })
      window.analytics?.track("Anonymous_FileUploadFailed", {
        fileName: file.name,
        error: err instanceof Error ? err.message : String(err),
      })
      setError(t("planOrder.fileErrors.processingFailed"))
      setIsProcessing(false)
    }
  }

  const content = (
    <DndProvider backend={HTML5Backend}>
      {/* Phone: room for the bottom tab bar; desktop: room for the left rail. */}
      <div className="min-h-screen bg-[#FAFAFA] pb-14 md:pb-0 md:pl-[48px]">
        {/* Sidebar (fixed) */}
        <AnonymousSidebar
          activeTab={activeTab}
          onTabChange={(tab) => {
            window.analytics?.track("Anonymous_TabSwitched", { tab })
            setActiveTab(tab)
          }}
        />

        {/* Main content area */}
        <div className="relative flex h-[calc(100dvh-3.5rem)] flex-col md:h-screen">
          {/* Loading overlay for shared route */}
          {isLoadingSharedRoute && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm md:left-[48px]">
              <div className="flex flex-col items-center space-y-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                <p className="text-sm text-gray-600">
                  {t("planOrder.routeToasts.loadingShared")}
                </p>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={getOrderFileAccept(anyFormatEnabled)}
            value={fileInputValue}
            onChange={handleFileInputChange}
            className="hidden"
          />

          {/* Plan tab content */}
          {activeTab === "plan" && (
            <>
              {/* Analyze Order Modal */}
              {isModalOpen && fileUrl && fileName && (
                <AnalyzeOrderModal
                  isProcessing={isProcessing}
                  fileUrl={fileUrl}
                  fileName={fileName}
                  isOpen={isModalOpen}
                  onClose={() => {
                    setIsModalOpen(false)
                    setIsPlanModalOpen(true)
                    setExtractedData(null)
                    setUploadedFile(null)
                    setError("")
                    setFileInputValue("")
                    setSharedRouteState(null)
                    setSharedDocumentStep("verify")
                  }}
                  initialAvoidOptions={selectedAvoidOptions}
                  initialLoadType={selectedLoadType}
                  initialVehicleType={selectedVehicleType}
                  initialDriver={selectedDriver}
                  extractedData={extractedData}
                  initialStep={sharedDocumentStep}
                  sharedRouteState={sharedRouteState}
                  onReset={resetComponentState}
                />
              )}

              {/* Plan Order Modal - auto opens */}
              {isPlanModalOpen && !isLoadingSharedRoute && (
                <PlaneOrderOnPageModal
                  isOpen={isPlanModalOpen}
                  onSwitchToAnalyzeOrder={handleSwitchToAnalyzeOrder}
                  onSwitchToTextOrder={handleOpenTextOrder}
                  onReset={resetComponentState}
                  onClose={() => {
                    setIsPlanModalOpen(false)
                    setSharedRouteState(null)
                  }}
                  sharedRouteState={sharedRouteState}
                  onRouteModified={handleRouteModified}
                />
              )}

              {/* Text Order Dialog */}
              <Dialog
                open={isTextOrderDialogOpen}
                onOpenChange={handleTextOrderDialogOpenChange}
              >
                <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t("planOrder.textOrder.title")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {t("planOrder.textOrder.description")}
                    </p>
                    <Textarea
                      placeholder={t("planOrder.textOrder.placeholder")}
                      value={textOrderContent}
                      onChange={(e) => setTextOrderContent(e.target.value)}
                      rows={15}
                      className="min-h-[400px] text-base"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={handleCloseTextOrder}
                      disabled={isProcessingTextOrder}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="ghost"
                      className="bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
                      onClick={handleSubmitTextOrder}
                      disabled={
                        isProcessingTextOrder || !textOrderContent.trim()
                      }
                    >
                      {isProcessingTextOrder
                        ? t("planOrder.textOrder.processing")
                        : t("planOrder.textOrder.analyze")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}

          {/* AI Chat tab content — kept mounted but hidden to preserve composing state */}
          {xmppConnected && myJid && transchatRoomJid && (
            <div
              className={activeTab === "ai" ? "flex h-full flex-col" : "hidden"}
            >
              <AnonymousChat
                roomJid={transchatRoomJid}
                client={window.XMPP!}
                myJid={parse(myJid)}
                onBeforeSend={showLeadModalForChat}
                onRouteLink={handleRouteLink}
              />
            </div>
          )}

          {/* Lead Capture Modal (shown on both tabs) */}
          <LeadCaptureModal
            isOpen={isLeadModalOpen}
            onClose={handleLeadModalClose}
          />
        </div>

        <CookieConsentBanner />
      </div>
    </DndProvider>
  )

  // Wrap with providers
  const wrappedContent = (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <XmppContext.Provider value={xmppContextValue}>
        {content}
      </XmppContext.Provider>
    </APIProvider>
  )

  return wrappedContent
}

export default AnalyzeOrderOnPage
